import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import type { Organization } from '@t3/shared';
import { ApiError, apiFetch } from '../api/client.ts';
import { useAuth } from '../auth/AuthContext.tsx';
import { Button } from '../components/Button.tsx';
import { Card } from '../components/Card.tsx';
import { FormField } from '../components/FormField.tsx';
import { useDocumentTitle } from '../components/useDocumentTitle.ts';

type StatusFilter = 'active' | 'inactive' | 'all';

function fmtCheckIn(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const date = d.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: d.getFullYear() === new Date().getFullYear() ? undefined : 'numeric',
  });
  const time = d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  return `${date}, ${time}`;
}

export function OrganizationsListPage(): React.ReactElement {
  useDocumentTitle('Organizations');
  const { agent } = useAuth();
  const isAdmin = agent?.role === 'admin';
  const [orgs, setOrgs] = useState<Organization[] | null>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('active');
  const [loadError, setLoadError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const [domain, setDomain] = useState('');
  const [createError, setCreateError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function load(filter: StatusFilter) {
    try {
      setOrgs(null);
      const res = await apiFetch<{ organizations: Organization[] }>(
        `/api/organizations?status=${filter}`,
      );
      setOrgs(res.organizations);
      setLoadError(null);
    } catch (err) {
      setLoadError(err instanceof ApiError ? err.message : 'Failed to load organizations.');
    }
  }

  useEffect(() => {
    void load(statusFilter);
  }, [statusFilter]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setCreateError(null);
    setBusy(true);
    try {
      await apiFetch('/api/organizations', {
        method: 'POST',
        json: { name: name.trim(), domain: domain.trim() || null },
      });
      setName('');
      setDomain('');
      setCreating(false);
      await load(statusFilter);
    } catch (err) {
      setCreateError(err instanceof ApiError ? err.message : 'Failed to create organization.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-[var(--color-header)]">Organizations</h1>
          <p className="mt-1 text-[var(--color-muted)]">Customer companies. Tickets and contacts attach to one of these.</p>
        </div>
        <div className="flex items-center gap-2">
          {isAdmin && (
            <a
              href="/api/organizations/export-block-hours.csv"
              download
              className="inline-flex items-center gap-1.5 rounded-[var(--radius-button)] border border-[var(--color-border)] bg-white px-3 py-2 text-sm font-medium text-[var(--color-body)] hover:border-[var(--color-t3-blue)] hover:text-[var(--color-t3-blue)]"
            >
              Download block-hours CSV
            </a>
          )}
          <Button onClick={() => setCreating((v) => !v)}>{creating ? 'Cancel' : 'New organization'}</Button>
        </div>
      </div>

      {creating && (
        <Card>
          <form onSubmit={submit} className="grid gap-4 sm:grid-cols-2">
            <FormField label="Name" name="name" required value={name} onChange={(e) => setName(e.target.value)} />
            <FormField
              label="Primary domain"
              name="domain"
              value={domain}
              onChange={(e) => setDomain(e.target.value)}
              hint="Optional. Used to match incoming emails to this org."
            />
            {createError && <p className="text-sm text-[var(--color-danger)] sm:col-span-2">{createError}</p>}
            <div className="sm:col-span-2">
              <Button type="submit" disabled={busy || !name.trim()}>
                {busy ? 'Creating…' : 'Create organization'}
              </Button>
            </div>
          </form>
        </Card>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs uppercase tracking-wide text-[var(--color-muted)]">Status:</span>
        {(['active', 'inactive', 'all'] as const).map((f) => (
          <button
            key={f}
            type="button"
            onClick={() => setStatusFilter(f)}
            className={
              statusFilter === f
                ? 'rounded-full bg-[var(--color-t3-blue)] px-3 py-1 text-xs font-semibold text-white'
                : 'rounded-full border border-[var(--color-border)] bg-white px-3 py-1 text-xs font-medium text-[var(--color-body)] hover:border-[var(--color-t3-blue)] hover:text-[var(--color-t3-blue)]'
            }
          >
            {f === 'active' ? 'Active' : f === 'inactive' ? 'Inactive' : 'All'}
          </button>
        ))}
      </div>

      {loadError && <p className="text-sm text-[var(--color-danger)]">{loadError}</p>}

      {orgs === null ? (
        <p className="text-[var(--color-muted)]">Loading…</p>
      ) : orgs.length === 0 ? (
        <Card>
          <p className="text-center text-[var(--color-muted)]">
            {statusFilter === 'inactive'
              ? 'No inactive organizations.'
              : statusFilter === 'all'
                ? 'No organizations yet. Create one to start logging tickets.'
                : 'No active organizations. Switch to All to see inactive ones.'}
          </p>
        </Card>
      ) : (
        <div className="overflow-hidden rounded-[var(--radius-card)] border border-[var(--color-border)] bg-white">
          <table className="w-full text-sm">
            <thead className="bg-[var(--color-surface)] text-left text-xs uppercase tracking-wide text-[var(--color-muted)]">
              <tr>
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Domain</th>
                <th className="px-4 py-3">Next check-in</th>
                <th className="px-4 py-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {orgs.map((o) => (
                <tr key={o.id} className="border-t border-[var(--color-border)]">
                  <td className="px-4 py-3">
                    <Link to={`/organizations/${o.id}`} className="font-medium text-[var(--color-t3-blue)] hover:underline">
                      {o.name}
                    </Link>
                  </td>
                  <td className="px-4 py-3 text-[var(--color-muted)]">{o.domain ?? '—'}</td>
                  <td className="px-4 py-3 text-[var(--color-body)]">{fmtCheckIn(o.nextCheckInAt)}</td>
                  <td className="px-4 py-3">{o.isActive ? 'Active' : 'Inactive'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
